import { MinusCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Form, Input, Select, Space, message } from 'antd';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pdfjs } from 'react-pdf';
import { envApiUrl } from '../../fetchHooks/envApiUrl';
import { useHealthmetrictypes } from '../../fetchHooks/useHealthmetrictypes';
import type { HealthMetricType } from '../../fetchHooks/useHealthmetrictypesWithMeasurements';
import { getHealthMetricDescription } from '../../utils/utilFunctions';
import { BloodworkImportModal, type ParsedBloodworkResult } from './BloodworkImportModal';
const { Option } = Select;

interface AddHealthMetricFormProps {
  onImportMeasurements?: (measurements: ParsedBloodworkResult[]) => void;
}

export const AddHealthMetricForm = ({ onImportMeasurements }: AddHealthMetricFormProps) => {
  const { t } = useTranslation();
  const { data: healthmetrictypes } = useHealthmetrictypes();
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedResults, setParsedResults] = useState<ParsedBloodworkResult[] | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!healthmetrictypes) return <></>;

  // PDF to image conversion using react-pdf - handles multi-page PDFs
  const convertPdfToImage = async (pdfFile: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;

          // Use pdfjs directly for simpler approach
          const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
          const numPages = pdf.numPages;

          console.log(`PDF has ${numPages} pages, combining all pages into single image`);

          // Create a master canvas that will contain all pages
          const masterCanvas = document.createElement('canvas');
          const masterContext = masterCanvas.getContext('2d');

          if (!masterContext) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          // Calculate total height needed for all pages
          let totalHeight = 0;
          let maxWidth = 0;
          const pageCanvases: HTMLCanvasElement[] = [];
          const pageViewports: any[] = [];

          // First pass: render all pages to individual canvases and calculate dimensions
          for (let pageNum = 1; pageNum <= numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const viewport = page.getViewport({ scale: 2.0 });

            const pageCanvas = document.createElement('canvas');
            const pageContext = pageCanvas.getContext('2d');

            if (!pageContext) {
              reject(new Error(`Could not get canvas context for page ${pageNum}`));
              return;
            }

            pageCanvas.height = viewport.height;
            pageCanvas.width = viewport.width;

            await page.render({
              canvasContext: pageContext,
              viewport: viewport,
              canvas: pageCanvas,
            }).promise;

            pageCanvases.push(pageCanvas);
            pageViewports.push(viewport);

            totalHeight += viewport.height;
            maxWidth = Math.max(maxWidth, viewport.width);
          }

          // Set master canvas dimensions
          masterCanvas.width = maxWidth;
          masterCanvas.height = totalHeight;

          // Fill with white background
          masterContext.fillStyle = '#ffffff';
          masterContext.fillRect(0, 0, maxWidth, totalHeight);

          // Second pass: combine all pages into master canvas
          let currentY = 0;
          for (let i = 0; i < pageCanvases.length; i++) {
            const pageCanvas = pageCanvases[i];
            const viewport = pageViewports[i];

            // Center the page horizontally if it's narrower than maxWidth
            const xOffset = (maxWidth - viewport.width) / 2;

            masterContext.drawImage(pageCanvas, xOffset, currentY);
            currentY += viewport.height;

            // Add a small separator line between pages (optional)
            if (i < pageCanvases.length - 1) {
              masterContext.strokeStyle = '#e0e0e0';
              masterContext.lineWidth = 1;
              masterContext.beginPath();
              masterContext.moveTo(0, currentY);
              masterContext.lineTo(maxWidth, currentY);
              masterContext.stroke();
              currentY += 2; // Small gap between pages
            }
          }

          // Convert master canvas to blob, then to File
          masterCanvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Could not convert canvas to blob'));
              return;
            }

            const imageFile = new File([blob], `${pdfFile.name.replace('.pdf', '')}-combined.png`, {
              type: 'image/png',
            });
            resolve(imageFile);
          }, 'image/png');
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Could not read PDF file'));
      reader.readAsArrayBuffer(pdfFile);
    });
  };

  // Common file processing logic
  const processFile = async (file: File) => {
    // Validate file type - support both images and PDFs
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];

    if (!allowedTypes.includes(file.type)) {
      message.error(t('healthMetric.pleaseSelectValidFile'));
      return;
    }

    // Validate file size (20MB limit)
    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      message.error(t('healthMetric.fileSizeTooLarge'));
      return;
    }

    setIsProcessing(true);
    message.loading(t('healthMetric.processingBloodworkFile'), 0);

    try {
      let fileToUpload = file;

      // If it's a PDF, convert to image first
      if (file.type === 'application/pdf') {
        message.loading(t('healthMetric.convertingPdfToImage'), 0);
        fileToUpload = await convertPdfToImage(file);
        message.loading(t('healthMetric.uploadingAndAnalyzing'), 0);
      }

      // Fetch CSRF token first
      const csrfResponse = await fetch(`${envApiUrl}/auth/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!csrfResponse.ok) {
        throw new Error('Failed to fetch CSRF token');
      }
      const { csrfToken } = await csrfResponse.json();

      const formData = new FormData();
      formData.append('file', fileToUpload);

      const response = await fetch(`${envApiUrl}/mydays/parse-bloodwork`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'x-csrf-token': csrfToken,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Parsed bloodwork results:', data);

      if (data.results && data.results.length > 0) {
        setParsedResults(data.results);
        setShowModal(true);
        message.success(t('healthMetric.successfullyParsedMeasurements', { count: data.results.length }));
      } else {
        message.warning(t('healthMetric.noMeasurementsFound'));
      }
    } catch (error) {
      console.error('Error processing bloodwork:', error);
      message.error(
        t('healthMetric.errorProcessingBloodwork', {
          error: error instanceof Error ? error.message : t('healthMetric.unknownError'),
        }),
      );
    } finally {
      setIsProcessing(false);
      message.destroy(); // Clear loading message
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  // Drag and drop handlers
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      await processFile(files[0]);
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setParsedResults(null);
  };

  const handleImportSelected = (selectedResults: ParsedBloodworkResult[]) => {
    if (onImportMeasurements) {
      onImportMeasurements(selectedResults);
    }
    setShowModal(false);
    setParsedResults(null);
    message.success(t('healthMetric.importedMeasurements', { count: selectedResults.length }));
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,image/jpeg,image/png,image/gif,image/webp,application/pdf"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />
      <Form.List name="measurements">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field) => (
              <AddMetricItem key={field.key} field={field} healthmetrictypes={healthmetrictypes} remove={remove} />
            ))}

            <Form.Item>
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                {t('healthMetric.addMeasurement')}
              </Button>
            </Form.Item>

            <Form.Item>
              <Button
                type="dashed"
                onClick={handleImportClick}
                block
                icon={<UploadOutlined />}
                loading={isProcessing}
                disabled={isProcessing}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  borderColor: isDragOver ? '#1890ff' : undefined,
                  backgroundColor: isDragOver ? '#f0f8ff' : undefined,
                  borderStyle: isDragOver ? 'dashed' : undefined,
                }}
              >
                {isProcessing
                  ? t('healthMetric.processingBloodwork')
                  : isDragOver
                    ? t('healthMetric.dropFileHere')
                    : t('healthMetric.importFromBloodwork')}
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      {parsedResults && healthmetrictypes && (
        <BloodworkImportModal
          visible={showModal}
          onCancel={handleModalCancel}
          onImport={handleImportSelected}
          results={parsedResults}
          healthmetrictypes={healthmetrictypes}
        />
      )}
    </>
  );
};

interface AddMetricItemProps {
  field: any;
  healthmetrictypes: HealthMetricType[];
  remove: any;
}

const AddMetricItem = ({ field, healthmetrictypes, remove }: AddMetricItemProps) => {
  const { t, i18n } = useTranslation();
  const form = Form.useFormInstance();
  const customFilterOption = (inputValue: string, option: any) =>
    option.children?.props?.children?.toLowerCase?.().indexOf(inputValue.toLowerCase()) >= 0;

  // Get current selected health metric type ID from form
  const healthmetrictypeId = form.getFieldValue(['measurements', field.name, 'healthmetrictypeId']);
  const selectedValue = healthmetrictypeId ? healthmetrictypes.find((item) => item.id === healthmetrictypeId) : null;

  return (
    <Space key={field.key} align="baseline">
      <Form.Item
        name={[field.name, 'healthmetrictypeId']}
        label={t('healthMetric.measurementType')}
        rules={[{ required: true, message: t('healthMetric.missingMeasurement') }]}
      >
        <Select
          style={{ width: 300 }}
          showSearch
          placeholder={t('healthMetric.selectHealthMetric')}
          filterOption={customFilterOption}
        >
          {healthmetrictypes.map((item) => (
            <Option key={item.id} value={item.id}>
              <span>{getHealthMetricDescription(item, i18n.language)}</span>
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name={[field.name, 'value']}
        label={t('healthMetric.value')}
        rules={[{ required: true, message: t('healthMetric.missingValue') }]}
      >
        <Input type="number" suffix={selectedValue?.unit || ''} />
      </Form.Item>
      <MinusCircleOutlined onClick={() => remove(field.name)} />
    </Space>
  );
};
